import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getLanguage } from 'react-switch-lang';
import en from '../../en.json';
import ma from '../../ma.json';
import * as myConstClass from '../../constant.js';
import './EventDetailsModal.css';

/**
 * Event Details Modal Component
 * Displays event details in a popup similar to Google Calendar
 */
const EventDetailsModal = ({ event, isOpen, onClose, onShare}) => {
    const language = getLanguage();
    const isMarathi = language === 'ma';
    
    const [eventDetails, setEventDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    useEffect(() => {
        if (isOpen && event && event.eventId) {
            fetchEventDetails();
        } else {
            setEventDetails(null);
            setError(null);
        }
    }, [isOpen, event]);
    
    const fetchEventDetails = async () => {
        if (!event || !event.eventId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            // Fetch event details from PHP API (same endpoint as EventDetailsPage)
            const userId = localStorage.getItem('user_id') || 0;
            const apiUrl = `${myConstClass.BASE_URL}event/getEventInfoFrontEnd/${event.eventId}/${language}/0/${userId}`;
            const response = await fetch(apiUrl);
            
            if (!response.ok) {
                throw new Error('Failed to fetch event details');
            }
            
            const data = await response.json();
            // Handle both direct object and wrapped response
            const eventData = data.data || data;
            setEventDetails(eventData);
        } catch (err) {
            console.error('Error fetching event details:', err);
            setError('Failed to load event details');
        } finally {
            setLoading(false);
        }
    };
    
    if (!isOpen || !event) return null;
    
    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        
        const eventDate = date instanceof Date ? date : new Date(date);
        if (isNaN(eventDate.getTime())) return '';
        
        const monthNames = isMarathi
            ? ['जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर']
            : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        
        const dayNames = isMarathi
            ? ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार']
            : ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        const dayName = dayNames[eventDate.getDay()];
        const monthName = monthNames[eventDate.getMonth()];
        const day = eventDate.getDate();
        const year = eventDate.getFullYear();
        
        return `${dayName}, ${monthName} ${day}, ${year}`;
    };
    
    // Get event start date
    // Priority: Use dateKey (YYYY-MM-DD) as it's the most reliable format
    // This avoids timezone issues when Date objects are serialized/deserialized
    const getEventStartDate = () => {
        // Priority 1: Use dateKey (YYYY-MM-DD format) - most reliable
        if (event.dateKey) {
            const [y, m, d] = event.dateKey.split('-');
            if (y && m && d) {
                // Parse in local time to avoid timezone issues
                return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
            }
        }
        
        // Priority 2: Use startDate if it's a Date object
        if (event.startDate) {
            if (event.startDate instanceof Date) {
                // If it's already a Date object, create a new one in local time
                const d = event.startDate;
                return new Date(d.getFullYear(), d.getMonth(), d.getDate());
            }
            
            // If it's a string, try to parse it
            // Check if it's in YYYY-MM-DD format first
            if (typeof event.startDate === 'string' && event.startDate.match(/^\d{4}-\d{2}-\d{2}/)) {
                const [y, m, d] = event.startDate.split('-');
                if (y && m && d) {
                    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                }
            }
            
            // Otherwise, try to parse as Date string (but this may have timezone issues)
            const parsed = new Date(event.startDate);
            if (!isNaN(parsed.getTime())) {
                // Convert to local date to avoid timezone issues
                return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
            }
        }
        
        return null;
    };
    
    // Helper to parse date string (YYYY-MM-DD or ISO) to local Date
    const parseDate = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
            return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());
        }
        if (typeof dateStr === 'string') {
            // Try to extract YYYY-MM-DD from common formats (including datetimes)
            const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                const y = parseInt(match[1], 10);
                const m = parseInt(match[2], 10);
                const d = parseInt(match[3], 10);
                const parsed = new Date(y, m - 1, d);
                if (!isNaN(parsed.getTime())) return parsed;
            }

            // Fallback: try Date.parse and convert to local date-only
            const parsedFallback = new Date(dateStr);
            if (!isNaN(parsedFallback.getTime())) {
                return new Date(parsedFallback.getFullYear(), parsedFallback.getMonth(), parsedFallback.getDate());
            }
        }
        return null;
    };

    // Get start and end dates - Priority order:
    // 1. Use event props directly (passed from calendar with startDateOriginal/endDateOriginal)
    // 2. Use eventDetails.arrSchedule (fetched from API)
    // 3. Fallback to computed from event.startDate
    let startDate = null;
    let endDate = null;
    let duration = 1;

    // Priority 1: Check if event prop has the original date fields (from calendar)
    if (event.startDateOriginal || event.endDateOriginal) {
        startDate = parseDate(event.startDateOriginal || event.startDate);
        endDate = parseDate(event.endDateOriginal || event.endDate);
        
        // Calculate duration if both dates available
        if (startDate && endDate) {
            const diffMs = endDate.getTime() - startDate.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            duration = Math.max(1, diffDays + 1); // inclusive
        } else if (event.duration) {
            duration = parseInt(event.duration) || 1;
            // Compute end date if we have start and duration but no end
            if (startDate && !endDate && duration > 1) {
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + duration - 1);
            }
        }
    }
    
    // Priority 2: Use eventDetails if available and we don't have dates yet
    if ((!startDate || !endDate) && eventDetails && eventDetails.arrSchedule && eventDetails.arrSchedule.length > 0) {
        const schedule = eventDetails.arrSchedule[0];
        
        if (!startDate) {
            startDate = parseDate(schedule.startDateOriginal || schedule.start_date || schedule.startDate || schedule.scheduleStartDate);
        }
        if (!endDate) {
            endDate = parseDate(schedule.endDateOriginal || schedule.end_date || schedule.endDate || schedule.scheduleEndDate || schedule.toDate);
        }
        
        // Calculate duration from dates if both available
        if (startDate && endDate) {
            const diffMs = endDate.getTime() - startDate.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            duration = Math.max(1, diffDays + 1); // inclusive
        } else if (schedule.duration) {
            duration = parseInt(schedule.duration) || 1;
            if (startDate && !endDate && duration > 1) {
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + duration - 1);
            }
        }
    }

    // If we have a startDate but still missing endDate, try to find a matching
    // schedule entry from the fetched `eventDetails.arrSchedule` that corresponds
    // to the occurrence the calendar opened (matching by scheduleId or start date).
    if (startDate && !endDate && eventDetails && eventDetails.arrSchedule && eventDetails.arrSchedule.length > 0) {
        let matched = null;

        // Try to match by scheduleId if provided on the event
        if (event.scheduleId) {
            matched = eventDetails.arrSchedule.find(s => String(s.scheduleId || s.id || s.schedule_id) === String(event.scheduleId));
        }

        // Otherwise try to match by start date (compare date-only)
        if (!matched) {
            matched = eventDetails.arrSchedule.find(s => {
                const sStart = parseDate(s.startDateOriginal || s.start_date || s.startDate || s.scheduleStartDate);
                return sStart && sStart.toDateString() === startDate.toDateString();
            });
        }

        if (matched) {
            const sEnd = parseDate(matched.endDateOriginal || matched.end_date || matched.endDate || matched.scheduleEndDate || matched.toDate);
            if (sEnd) {
                endDate = sEnd;
            } else if (matched.duration && parseInt(matched.duration, 10) > 1) {
                const md = parseInt(matched.duration, 10) || 1;
                const ed = new Date(startDate);
                ed.setDate(ed.getDate() + md - 1);
                endDate = ed;
            }
        }
    }

    // Priority 3: Fallback to event prop if still no dates
    if (!startDate) {
        startDate = getEventStartDate();
        duration = event.duration || 1;
        
        // Calculate end date from duration
        endDate = startDate ? new Date(startDate) : null;
        if (endDate && duration > 1) {
            endDate.setDate(endDate.getDate() + duration - 1);
        }
    }
    
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    
    return (
        <div className="event-details-modal-overlay" onClick={handleBackdropClick}>
            <div className="event-details-modal" onClick={(e) => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="event-details-modal-header">
                    <div className="event-details-modal-title-section">
                        <h3 className="event-details-modal-title">{event.name}</h3>
                        {event.status === 'Full' && (
                            <span className="event-status-badge event-status-full">Full</span>
                        )}
                        {event.status === 'Open' && (
                            <span className="event-status-badge event-status-open">Open</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {onShare && (
                            <button
                                className="event-details-modal-share"
                                onClick={(e) => onShare(event, e)}
                                aria-label="Share event"
                                title="Share"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 8 }}
                            >
                                <i className="fas fa-share-alt"></i>
                            </button>
                        )}
                        <button className="event-details-modal-close" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>
                
                {/* Modal Body */}
                <div className="event-details-modal-body">
                    {loading && (
                        <div className="event-details-loading">
                            <i className="fas fa-spinner fa-spin"></i>
                            <span>Loading event details...</span>
                        </div>
                    )}
                    
                    {error && (
                        <div className="event-details-error">
                            <i className="fas fa-exclamation-circle"></i>
                            <span>{error}</span>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {/* Date and Time */}
                            <div className="event-details-section">
                                <div className="event-details-icon">
                                    <i className="far fa-calendar"></i>
                                </div>
                                <div className="event-details-content">
                                    <div className="event-details-label">Date</div>
                                    <div className="event-details-value">
                                        {startDate && formatDate(startDate)}
                                        {duration > 1 && endDate && (
                                            <span> - {formatDate(endDate)}</span>
                                        )}
                                    </div>
                                    {duration > 1 && (
                                        <div className="event-details-duration">
                                            {duration} day{duration > 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Location */}
                            {event.location && (
                                <div className="event-details-section">
                                    <div className="event-details-icon">
                                        <i className="fas fa-map-marker-alt"></i>
                                    </div>
                                    <div className="event-details-content">
                                        <div className="event-details-label">Location</div>
                                        <div className="event-details-value">{event.location}</div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Language */}
                            {event.language && (
                                <div className="event-details-section">
                                    <div className="event-details-icon">
                                        <i className="fas fa-language"></i>
                                    </div>
                                    <div className="event-details-content">
                                        <div className="event-details-label">Language</div>
                                        <div className="event-details-value">{event.language}</div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Event Details from API */}
                            {eventDetails && (
                                <>
                                    {/* Description */}
                                    {eventDetails.description && (
                                        <div className="event-details-section event-details-description">
                                            <div className="event-details-content">
                                                <div className="event-details-label">Description</div>
                                                <div 
                                                    className="event-details-value event-details-html"
                                                    dangerouslySetInnerHTML={{ __html: eventDetails.description }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Price */}
                                    {(eventDetails.first_price || eventDetails.second_price) && (
                                        <div className="event-details-section">
                                            <div className="event-details-icon">
                                                <i className="fas fa-rupee-sign"></i>
                                            </div>
                                            <div className="event-details-content">
                                                <div className="event-details-label">Price</div>
                                                <div className="event-details-value">
                                                    {eventDetails.first_price && (
                                                        <span>
                                                            ₹{eventDetails.first_price}
                                                            {eventDetails.first_price_for && ` (${eventDetails.first_price_for})`}
                                                        </span>
                                                    )}
                                                    {eventDetails.first_price && eventDetails.second_price && ' / '}
                                                    {eventDetails.second_price && (
                                                        <span>
                                                            ₹{eventDetails.second_price}
                                                            {eventDetails.second_price_for && ` (${eventDetails.second_price_for})`}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Event Type */}
                                    {eventDetails.event_type && (
                                        <div className="event-details-section">
                                            <div className="event-details-icon">
                                                <i className="fas fa-tag"></i>
                                            </div>
                                            <div className="event-details-content">
                                                <div className="event-details-label">Event Type</div>
                                                <div className="event-details-value">{eventDetails.event_type}</div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
                
                {/* Modal Footer */}
                <div className="event-details-modal-footer">
                    <Link 
                        to={{
                            pathname: `/event_details/${event.eventId}`,
                            state: { selectedScheduleId: event.scheduleId }
                        }}
                        className="event-details-view-full-btn"
                        onClick={onClose}
                    >
                        {isMarathi ? ma.home['View full details'] : en.home['View full details']}
                    </Link>
                </div>
            </div>
        </div>
    );
};

EventDetailsModal.propTypes = {
    event: PropTypes.shape({
        eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        location: PropTypes.string,
        language: PropTypes.string,
        duration: PropTypes.number,
        status: PropTypes.string,
        startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        dateKey: PropTypes.string
    }),
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onShare: PropTypes.func
};

EventDetailsModal.defaultProps = {
    event: null,
    isOpen: false,
    onShare: null
};

export default EventDetailsModal;

